// @ts-nocheck
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export interface College {
  id: string;
  name: string;
  city: string;
  state: string;
  division: string;
  conference: string;
  public_private: string;
  school_logo: string;
  isSaved?: boolean;
  average_gpa: number | null;
  average_sat_score: number | null;
  average_act_score: number | null;
  sat_25th_percentile: number | null;
  sat_75th_percentile: number | null;
  act_25th_percentile: number | null;
  act_75th_percentile: number | null;
  undergraduate_enrollment: number | null;
  in_state_tuition: number | null;
  out_state_tuition: number | null;
  admission_rate: number | null;
  graduation_rate: number | null;
  religious_affiliation: string | null;
  main_website: string | null;
  application_website: string | null;
  admissions_website: string | null;
  campus: string | null;
  colors: string | null;
  mascot: string | null;
  male_female_ratio: string | null;
  programs_of_study: string | null;
  has_campus_housing: boolean | null;
}

interface SavedCollege {
  id: string;
  college: College;
  isSaved: boolean;
}

export interface CollegeFilters {
  states: string[];
  divisions: string[];
  conferences: string[];
  religiousAffiliations: string[];
  tuitionMin: number | null;
  tuitionMax: number | null;
  satMin: number | null;
  satMax: number | null;
  actMin: number | null;
  actMax: number | null;
  gpaMin: number | null;
  gpaMax: number | null;
  sports: string[];
  searchQuery: string;
}

// Define filter options type
export interface CollegeFilterOptions {
  states: string[];
  divisions: string[];
  conferences: string[];
  religiousAffiliations: string[];
  sports: { label: string; columnMen: string; columnWomen: string }[];
}
interface SavedCollegeRecord {
  id: string;
  profile_id: string | null;
  college_id: string | null;
  notes: string | null;
  saved_at: string | null;
  status: string | null;
}

// Define an insert-specific type with only fields needed for insertion
interface SavedCollegeInsert {
  profile_id: string;
  college_id: string;
  saved_at: string;
  notes?: string | null;
  status?: string | null;
}

interface RecruitingTrackerInsert {
  profile_id: string;
  college_id: string;
  tracker_phase: string;
  created_at: string;
  last_contact_date: string;
}

export interface UseCollegeSearchReturn {
  colleges: College[];
  suggestedColleges: College[];
  savedColleges: College[];
  loading: boolean;
  error: string | null;
  searchColleges: (searchTerm: string) => Promise<void>;
  fetchSuggestedColleges: () => Promise<void>;
  toggleSaveCollege: (collegeId: string) => Promise<any>;
  fetchSavedColleges: () => Promise<void>;
  searchResults: College[];
  isSearching: boolean;
  loadingMore: boolean;
  totalCount: number;
  filters: CollegeFilters;
  filterOptions: {
    states: string[];
    divisions: string[];
    conferences: string[];
    religiousAffiliations: string[];
    sports: { label: string; columnMen: string; columnWomen: string }[];
    tuitionRange?: [number, number];
    actRange?: [number, number]; 
    satRange?: [number, number];
    gpaRange?: [number, number];
  };
  hasMore: boolean;
  setFilters: React.Dispatch<React.SetStateAction<CollegeFilters>>;
  loadMore: () => Promise<void>;
  loadColleges: () => Promise<void>;
}

export const toggleSaveCollege = async (collegeId: string, profileId: string) => {
  console.log(`Toggle save college: ${collegeId} for profile: ${profileId}`);
  try {
    const { data: existingData, error: checkError } = await supabase
      .from('saved_colleges')
      .select<string, SavedCollegeRecord>('id')
      .eq('college_id', collegeId)
      .eq('profile_id', profileId)
      .maybeSingle();

    if (checkError) return { success: false, error: checkError };

    if (existingData) {
      const { error: deleteError } = await supabase
        .from('saved_colleges')
        .delete()
        .eq('college_id', collegeId)
        .eq('profile_id', profileId);

      if (deleteError) return { success: false, error: deleteError };
      return { success: true, action: 'removed' };
    } else {
      const savedCollege: SavedCollegeInsert = {
        college_id: collegeId,
        profile_id: profileId,
        saved_at: new Date().toISOString()
      };

      const { error: insertError } = await supabase
        .from('saved_colleges')
        .insert<SavedCollegeInsert>(savedCollege);

      if (insertError) return { success: false, error: insertError };

      // Now let's add to recruiting tracker with better debugging
      console.log(`Adding to recruiting tracker - college_id: ${collegeId}, profile_id: ${profileId}`);
      const trackerData: RecruitingTrackerInsert = {
        profile_id: profileId,
        college_id: collegeId,
        tracker_phase: 'interested',
        created_at: new Date().toISOString(),
        last_contact_date: new Date().toISOString()
      };
      
      console.log("Tracker data to insert:", trackerData);
      
      const { data: trackerResult, error: trackerError } = await supabase
        .from('recruiting_tracker')
        .upsert<RecruitingTrackerInsert>(trackerData, { 
          onConflict: 'profile_id,college_id',
          ignoreDuplicates: false 
        });

      if (trackerError) {
        console.error("Error adding to recruiting tracker:", trackerError);
        toast.error("College saved but couldn't add to tracker");
        return { success: true, action: 'added', trackerError };
      }
      
      console.log("Successfully added to recruiting tracker:", trackerResult);
      return { success: true, action: 'added' };
    }
  } catch (error) {
    console.error("Exception in toggleSaveCollege:", error);
    return { success: false, error };
  }
};

export const useCollegeSearch = (): UseCollegeSearchReturn => {
  const [colleges, setColleges] = useState<College[]>([]);
  const [suggestedColleges, setSuggestedColleges] = useState<College[]>([]);
  const [savedColleges, setSavedColleges] = useState<College[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<College[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const { profile } = useAuth();
  
  // Pagination state
  const [limit, setLimit] = useState<number>(25);
  const [offset, setOffset] = useState<number>(0);
  
  const [filters, setFilters] = useState<CollegeFilters>({
    states: [],
    divisions: [],
    conferences: [],
    religiousAffiliations: [],
    tuitionMin: null,
    tuitionMax: null,
    satMin: null,
    satMax: null,
    actMin: null,
    actMax: null,
    gpaMin: null,
    gpaMax: null,
    sports: [],
    searchQuery: ''
  });
  
  const [filterOptions, setFilterOptions] = useState<CollegeFilterOptions>({
    states: [
      'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 
      'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 
      'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 
      'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 
      'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 
      'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 
      'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
      'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 
      'West Virginia', 'Wisconsin', 'Wyoming'
    ],
    divisions: ['NCAA D1', 'NCAA D2', 'NCAA D3', 'NAIA', 'NJCAA', 'NCCAA', 'CCCAA', 'Other'],
    conferences: ['ACC', 'Big 10', 'Big 12', 'Pac-12', 'SEC'],
    religiousAffiliations: [
      'None', 'Roman Catholic Church', 'Baptist Church', 'Presbyterian Church', 
      'Methodist Church', 'Lutheran Church', 'Episcopal Church', 'Christian', 
      'Protestant', 'Jewish', 'Church of Christ', 'Evangelical Lutheran Church in America', 
      'United Methodist Church', 'Assemblies of God', 'Church of the Nazarene'
    ],
    sports: [
      { label: 'Baseball', columnMen: 'baseball_men', columnWomen: 'baseball_women' },
      { label: 'Basketball', columnMen: 'basketball_men', columnWomen: 'basketball_women' },
      { label: 'Beach Volleyball', columnMen: 'beach_volleyball_men', columnWomen: 'beach_volleyball_women' },
      { label: 'Bowling', columnMen: 'bowling_men', columnWomen: 'bowling_women' },
      { label: 'Cross Country', columnMen: 'track_field_xcountry_men', columnWomen: 'track_field_xcountry_women' },
      { label: 'Diving', columnMen: 'diving_men', columnWomen: 'diving_women' },
      { label: 'Equestrian', columnMen: 'equestrian_men', columnWomen: 'equestrian_women' },
      { label: 'Fencing', columnMen: 'fencing_men', columnWomen: 'fencing_women' },
      { label: 'Field Hockey', columnMen: 'field_hockey_men', columnWomen: 'field_hockey_women' },
      { label: 'Football', columnMen: 'football_men', columnWomen: 'football_women' },
      { label: 'Golf', columnMen: 'golf_men', columnWomen: 'golf_women' },
      { label: 'Gymnastics', columnMen: 'gymnastics_men', columnWomen: 'gymnastics_women' },
      { label: 'Ice Hockey', columnMen: 'ice_hockey_men', columnWomen: 'ice_hockey_women' },
      { label: 'Lacrosse', columnMen: 'lacrosse_men', columnWomen: 'lacrosse_women' },
      { label: 'Rowing', columnMen: 'rowing_men', columnWomen: 'rowing_women' },
      { label: 'Soccer', columnMen: 'soccer_men', columnWomen: 'soccer_women' },
      { label: 'Softball', columnMen: 'softball_men', columnWomen: 'softball_women' },
      { label: 'Swimming', columnMen: 'swimming_men', columnWomen: 'swimming_women' },
      { label: 'Tennis', columnMen: 'tennis_men', columnWomen: 'tennis_women' },
      { label: 'Track & Field (Indoor)', columnMen: 'track_field_indoor_men', columnWomen: 'track_field_indoor_women' },
      { label: 'Track & Field (Outdoor)', columnMen: 'track_field_outdoor_men', columnWomen: 'track_field_outdoor_women' },
      { label: 'Volleyball', columnMen: 'volleyball_men', columnWomen: 'volleyball_women' },
      { label: 'Water Polo', columnMen: 'water_polo_men', columnWomen: 'water_polo_women' },
      { label: 'Wrestling', columnMen: 'wrestling_men', columnWomen: 'wrestling_women' },
    ]
  });

  const searchColleges = async (searchTerm: string) => {
    setIsSearching(true);
    setError(null);
    try {
      // Reset pagination when searching
      setOffset(0);
      setLimit(25);
      
      let query = supabase
        .from("colleges")
        .select<string, College>('*', { count: 'exact' })
        .order('name', { ascending: true })
        .range(0, 24); // Get first 25 results (index 0-24)
      
      if (searchTerm) {
        query = query.ilike('name', `%${searchTerm}%`);
      }
      
      const { data, error, count } = await query;

      console.log("🎓 Colleges returned:", data);

      if (error) {
        setError("Failed to fetch colleges.");
      } else if (data) {
        // Using proper typing with data directly
        setColleges(data);
        setSearchResults(data);
        
        if (count !== null) {
          setTotalCount(count);
          setHasMore(data.length < count);
        }
      }
    } catch (err) {
      setError("An unexpected error occurred.");
    } finally {
      setIsSearching(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    setError(null);
    
    try {
      const newOffset = offset + limit;
      setOffset(newOffset);
      
      let query = supabase
        .from("colleges")
        .select<string, College>('*')
        .order('name', { ascending: true })
        .range(newOffset, newOffset + limit - 1);
      
      // Apply filters if they exist
      if (filters.searchQuery && filters.searchQuery.trim() !== '') {
        query = query.ilike('name', `%${filters.searchQuery.trim()}%`);
      }
      
      if (filters.states.length > 0) {
        query = query.in('state', filters.states);
      }
      
      if (filters.divisions.length > 0) {
        query = query.in('division', filters.divisions);
      }
      
      if (filters.conferences.length > 0) {
        query = query.in('conference', filters.conferences);
      }
      
      // Apply religious affiliation filters
      if (filters.religiousAffiliations.length > 0) {
        query = query.in('religious_affiliation', filters.religiousAffiliations);
      }
      
      // Apply tuition filters
      if (filters.tuitionMin !== null) {
        query = query.gte('out_state_tuition', filters.tuitionMin);
      }
      if (filters.tuitionMax !== null) {
        query = query.lte('out_state_tuition', filters.tuitionMax);
      }
      
      // Apply SAT score filters
      if (filters.satMin !== null) {
        query = query.gte('average_sat_score', filters.satMin);
      }
      if (filters.satMax !== null) {
        query = query.lte('average_sat_score', filters.satMax);
      }
      
      // Apply ACT score filters
      if (filters.actMin !== null) {
        query = query.gte('average_act_score', filters.actMin);
      }
      if (filters.actMax !== null) {
        query = query.lte('average_act_score', filters.actMax);
      }
      
      // Apply GPA filters
      if (filters.gpaMin !== null) {
        query = query.gte('average_gpa', filters.gpaMin);
      }
      if (filters.gpaMax !== null) {
        query = query.lte('average_gpa', filters.gpaMax);
      }
      
      const { data, error } = await query;

      if (error) {
        setError("Failed to load more colleges.");
      } else if (data) {
        // Using proper typing with data directly
        const newColleges = data;
        console.log(`🎓 Loaded ${newColleges.length} more colleges`);
        
        // Append the new colleges to the existing list
        setColleges(prev => [...prev, ...newColleges]);
        setSearchResults(prev => [...prev, ...newColleges]);
        
        // Check if we've loaded all colleges
        setHasMore(newColleges.length === limit);
      }
    } catch (err) {
      setError("An unexpected error occurred while loading more colleges.");
    } finally {
      setLoadingMore(false);
    }
  };

  const fetchSuggestedColleges = async () => {
    if (!profile?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data: savedData } = await supabase
        .from("saved_colleges")
        .select<string, SavedCollegeRecord>('college_id')
        .eq("profile_id", profile.id);

      const savedCollegeIds = savedData?.map((item) => item.college_id) || [];

      const { data } = await supabase
        .from("colleges")
        .select<string, College>('*')
        .not("id", "in", savedCollegeIds.length > 0 ? `(${savedCollegeIds.join(",")})` : '(null)')
        .limit(25);

      if (data) {
        setSuggestedColleges(data);
      }
    } catch (err) {
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedColleges = async () => {
    if (!profile?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await supabase
        .from("saved_colleges")
        .select<string, SavedCollege>('id, college:college_id (*)')
        .eq("profile_id", profile.id);

      const collegesWithSaveStatus = (data || []).map((item) => ({
        ...item.college,
        isSaved: true
      }));

      setSavedColleges(collegesWithSaveStatus);
    } catch (err) {
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const loadColleges = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Reset pagination when applying filters
      setOffset(0);
      
      let query = supabase
        .from("colleges")
        .select<string, College>('*', { count: 'exact' })
        .order('name', { ascending: true })
        .range(0, limit - 1);
      
      // Apply search query filter
      if (filters.searchQuery && filters.searchQuery.trim() !== '') {
        query = query.ilike('name', `%${filters.searchQuery.trim()}%`);
      }
      
      // Apply location filters
      if (filters.states.length > 0) {
        console.log('🏞️ Applying state filter:', filters.states);
        query = query.in('state', filters.states);
      }
      
      // Apply division filters
      if (filters.divisions.length > 0) {
        console.log('🎯 Applying division filter:', filters.divisions);
        query = query.in('division', filters.divisions);
      }
      
      // Apply conference filters
      if (filters.conferences.length > 0) {
        query = query.in('conference', filters.conferences);
      }
      
      // Apply religious affiliation filters
      if (filters.religiousAffiliations.length > 0) {
        query = query.in('religious_affiliation', filters.religiousAffiliations);
      }
      
      // Apply tuition filters
      if (filters.tuitionMin !== null) {
        query = query.gte('out_state_tuition', filters.tuitionMin);
      }
      if (filters.tuitionMax !== null) {
        query = query.lte('out_state_tuition', filters.tuitionMax);
      }
      
      // Apply SAT score filters
      if (filters.satMin !== null) {
        query = query.gte('average_sat_score', filters.satMin);
      }
      if (filters.satMax !== null) {
        query = query.lte('average_sat_score', filters.satMax);
      }
      
      // Apply ACT score filters
      if (filters.actMin !== null) {
        query = query.gte('average_act_score', filters.actMin);
      }
      if (filters.actMax !== null) {
        query = query.lte('average_act_score', filters.actMax);
      }
      
      // Apply GPA filters
      if (filters.gpaMin !== null) {
        query = query.gte('average_gpa', filters.gpaMin);
      }
      if (filters.gpaMax !== null) {
        query = query.lte('average_gpa', filters.gpaMax);
      }
      
      // Apply sports filters - filter colleges that offer the selected sport (men's or women's)
      if (filters.sports.length > 0) {
        // For each selected sport, we need to find the column names and filter
        const sportOption = filterOptions.sports.find(s => s.label === filters.sports[0]);
        if (sportOption) {
          // Use OR filter - college offers sport for either men or women
          query = query.or(`${sportOption.columnMen}.eq.true,${sportOption.columnWomen}.eq.true`);
        }
      }
      
      console.log('🔍 Applying filters:', filters);
      
      const { data, error, count } = await query;
      
      console.log('🎓 Raw query result count:', count);
      console.log('🎓 Raw query result data sample:', data?.slice(0, 3));
      
      if (error) {
        console.error('Database query error:', error);
        setError("Failed to fetch colleges with filters.");
      } else if (data) {
        console.log(`🎓 Loaded ${data.length} colleges with filters`);
        setColleges(data);
        setSearchResults(data);
        
        if (count !== null) {
          setTotalCount(count);
          setHasMore(data.length < count);
        }
      }
    } catch (err) {
      console.error('Unexpected error in loadColleges:', err);
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profile?.id) fetchSavedColleges();
  }, [profile?.id]);

  const handleToggleSaveCollege = async (collegeId: string) => {
    if (!profile?.id) {
      toast.error("You must be logged in to save colleges.");
      return { success: false };
    }

    const result = await toggleSaveCollege(collegeId, profile.id);
    if (result.success) {
      const toggleSave = (list: College[]) =>
        list.map((college) =>
          college.id === collegeId ? { ...college, isSaved: result.action === 'added' } : college
        );

      setColleges((prev) => toggleSave(prev));
      setSuggestedColleges((prev) => toggleSave(prev));
      setSearchResults((prev) => toggleSave(prev));

      // Fetch updated saved colleges if a college was added
      if (result.action === 'added') {
        fetchSavedColleges();
      } else { 
        // Remove from saved colleges if it was removed
        setSavedColleges((prev) => prev.filter((c) => c.id !== collegeId));
      }
      
      // Show toast with more specific info about tracker
      if (result.trackerError) {
        toast.error(`College saved but couldn't add to tracker: ${result.trackerError.message}`);
      } else {
        toast.success(result.action === 'added' ? 'College saved' : 'College removed');
      }
    } else if (result.error) {
      toast.error(`Error: ${result.error.message || 'Failed to save college'}`);
    }
    return result;
  };

  return {
    colleges,
    suggestedColleges,
    savedColleges,
    loading,
    error,
    searchColleges,
    fetchSuggestedColleges,
    toggleSaveCollege: handleToggleSaveCollege,
    fetchSavedColleges,
    searchResults,
    isSearching,
    loadingMore,
    totalCount,
    filters,
    filterOptions,
    hasMore,
    setFilters,
    loadMore,
    loadColleges
  };
};
